export interface Translations {
  bubble: {
    open: string;
    close: string;
    badge: string;
    dragHint: string;
  };
  panel: {
    closeAria: string;
    languageAria: string;
    welcomeFallback: string;
    quickQuestionsTitle: string;
    /** 数字人聊天区快速提问栏标题(固定文案,不取后台引导语) */
    quickAskTitle: string;
    inputPlaceholder: string;
    sendButton: string;
    sendAria: string;
    onlineStatus: string;
    thinkingAria: string;
    /** AI 回合进行中(思考/流式/口播)时发送闸门提示文案 */
    aiBusyHint: string;
    /** 留资卡片被 dismiss 后,消息流里出现的可点击 pill 文案,点击会重新打开卡片 */
    leadReopenHint: string;
    /** 欢迎屏「立即咨询」CTA 按钮文案(数字人沉浸式 UI) */
    startChat: string;
    /** 欢迎屏快速了解区提示,guideMsg 缺省时兜底 */
    welcomeQuickHint: string;
    /** 数字人视频未就绪(没生成成功 / 加载失败)时左侧兜底提示 */
    videoPreparing: string;
    /** 后台标签/任务栏收到新消息(AI 或真人)时,标题闪烁 + 系统通知标题显示的提示语(含 💬 前缀) */
    newMessageTitle: string;
    /** 系统桌面通知正文:提示有新消息可点击查看 */
    newMessageBody: string;
  };
  roles: {
    visitor: string;
    ai: string;
    human: string;
    system: string;
  };
  web: {
    aiChat: string;
    humanChat: string;
    switchToHuman: string;
    switchToAI: string;
    resetChat: string;
    commonQuestions: string;
    faqTitle: string;
    selectLanguage: string;
    maximize: string;
    restore: string;
    offlineLabel: string;
    aiGreeting: string;
    humanGreeting: string;
    agentOffline: string;
    agentOfflineMessage: string;
    wechatPrompt: string;
    wechatHint: string;
    serviceHoursLabel: string;
    /** 图片消息点击放大的提示 / aria 文案 */
    viewImage: string;
    /** 视频消息缩略图的播放 aria 文案 */
    playVideo: string;
    /** 放大查看遮罩的关闭按钮 aria 文案 */
    closeViewer: string;
    /** 文件类消息下载按钮的 aria/title 文案 */
    downloadFile: string;
    /** 文件类型描述(历史消息无原始文件名时作 fallback 展示) */
    fileTypePdf: string;
    fileTypeWord: string;
    fileTypeExcel: string;
    fileTypePpt: string;
    fileTypeArchive: string;
    fileTypeGeneric: string;
  };
  /** 留资卡(LeadCaptureCard)全部文案:标题/字段/校验/按钮/提交成功致谢 */
  leadCard: {
    ariaForm: string;
    title: string;
    nameLabel: string;
    namePlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    phoneError: string;
    submit: string;
    submitting: string;
    dismiss: string;
    success: string;
  };
}

export const zh: Translations = {
  bubble: {
    open: '打开客服',
    close: '关闭客服',
    badge: '在线',
    dragHint: '长按可拖动',
  },
  panel: {
    closeAria: '关闭客服面板',
    languageAria: '选择语言',
    welcomeFallback: '您好,有什么可以帮您?',
    quickQuestionsTitle: '您可以试试问我以下问题',
    quickAskTitle: '快速提问',
    inputPlaceholder: '请输入您的问题...',
    sendButton: '发送',
    sendAria: '发送消息',
    onlineStatus: '在线',
    thinkingAria: '思考中',
    aiBusyHint: '客服正在回复中，请稍候…',
    leadReopenHint: '需要时,点此留下联系方式',
    startChat: '立即咨询',
    welcomeQuickHint: '快速了解',
    videoPreparing: '视频资源准备中…',
    newMessageTitle: '💬 新消息',
    newMessageBody: '您有新消息,点此查看。',
  },
  roles: {
    visitor: '访客',
    ai: 'AI 助手',
    human: '人工客服',
    system: '系统',
  },
  web: {
    aiChat: 'AI 客服',
    humanChat: '人工客服',
    switchToHuman: '转人工',
    switchToAI: '返回 AI',
    resetChat: '重新开始',
    commonQuestions: '常见问题',
    faqTitle: '常见问题解答',
    selectLanguage: '选择语言',
    maximize: '最大化',
    restore: '还原',
    offlineLabel: '离线',
    aiGreeting: '您好,我是 AI 客服,很高兴为您服务!请问有什么可以帮您?',
    humanGreeting: '您好,已为您接入人工客服,请问有什么可以帮您?',
    agentOffline: '客服不在线',
    agentOfflineMessage: '抱歉,人工客服暂时不在线。您可以扫描下方二维码添加我们的微信,我们会尽快为您解答。',
    wechatPrompt: '扫码添加微信客服',
    wechatHint: '使用微信扫一扫添加客服',
    serviceHoursLabel: '人工客服在线时间: ',
    viewImage: '查看大图',
    playVideo: '播放视频',
    closeViewer: '关闭',
    downloadFile: '下载',
    fileTypePdf: 'PDF 文档',
    fileTypeWord: 'Word 文档',
    fileTypeExcel: 'Excel 表格',
    fileTypePpt: 'PPT 演示文稿',
    fileTypeArchive: '压缩文件',
    fileTypeGeneric: '文件',
  },
  leadCard: {
    ariaForm: '留资表单',
    title: '留下您的联系方式',
    nameLabel: '姓名',
    namePlaceholder: '请输入您的姓名',
    phoneLabel: '手机号',
    phonePlaceholder: '请输入您的手机号',
    phoneError: '请输入正确的 11 位手机号',
    submit: '提交',
    submitting: '提交中…',
    dismiss: '稍后再说',
    success: '已收到您的联系方式，稍后会有顾问联系您。',
  },
};
